import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '../../hooks/useConfirm';
import Layout from '../../components/Layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { formConfigService } from '../../services/form-config.service';
import { listingCategoriesService } from '../../services/listing-categories.service';
import { ListingCategory } from '../../interfaces/listing-category.interface';
import {
  FormConfigSummary,
  FORM_TYPE_LABELS,
  FormType,
} from '../../interfaces/form-config.interface';
import { getErrorMsg } from '../../utils/error';
import { FORM_TYPE_ICONS } from './form-config.constants';

const UNCATEGORIZED_KEY = '__uncategorized__';

// ── Config card ───────────────────────────────────────────────────────────────

interface ConfigCardProps {
  config: FormConfigSummary;
  generating: boolean;
  onGenerate: () => void;
  onEdit: () => void;
}

const ConfigCard = ({ config, generating, onGenerate, onEdit }: ConfigCardProps) => (
  <Card className="p-4 flex flex-col gap-3">
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-2">
        <span className="text-2xl" role="img" aria-label={config.formType}>
          {FORM_TYPE_ICONS[config.formType] ?? '📋'}
        </span>
        <div>
          <div className="font-semibold text-gray-900 dark:text-gray-100">
            {FORM_TYPE_LABELS[config.formType] ?? config.formType}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            v{config.version}
          </div>
        </div>
      </div>
      <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
        config.isActive
          ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
          : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
      }`}>
        {config.isActive ? 'Active' : 'Inactive'}
      </span>
    </div>

    <div className="text-sm text-gray-600 dark:text-gray-400 space-y-0.5">
      <div className="flex items-center gap-1.5">
        <span>{config.fieldCount} fields</span>
        {config.fieldCount === 0 && (
          <span className="px-1.5 py-0.5 text-xs rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400 font-medium">
            No fields
          </span>
        )}
      </div>
      <div>Updated {new Date(config.updatedAt).toLocaleDateString()}</div>
    </div>

    <div className="mt-auto flex flex-col gap-1.5">
      {config.fieldCount === 0 && (
        <Button
          size="sm"
          className="w-full"
          disabled={generating}
          onClick={onGenerate}
        >
          {generating ? 'Generating…' : 'Generate Defaults'}
        </Button>
      )}
      <Button
        variant="secondary"
        size="sm"
        className="w-full"
        onClick={onEdit}
      >
        Edit Fields
      </Button>
    </div>
  </Card>
);

// ── Main page ─────────────────────────────────────────────────────────────────

const FormConfigList = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { confirm, ConfirmDialog } = useConfirm();
  const [configs, setConfigs] = useState<FormConfigSummary[]>([]);
  const [categories, setCategories] = useState<ListingCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [flushSuccess, setFlushSuccess] = useState(false);
  const [flushing, setFlushing] = useState(false);
  const [generatingType, setGeneratingType] = useState<FormType | null>(null);

  const handleFlushCache = async () => {
    setFlushing(true);
    setFlushSuccess(false);
    setError('');
    try {
      await formConfigService.flushCache();
      setFlushSuccess(true);
      setTimeout(() => setFlushSuccess(false), 4000);
    } catch (e) {
      setError(getErrorMsg(e, 'Flush failed'));
    } finally {
      setFlushing(false);
    }
  };

  const handleGenerateDefaults = async (config: FormConfigSummary) => {
    if (!await confirm(t('confirm_generate_defaults'))) return;
    setGeneratingType(config.formType);
    try {
      const updated = await formConfigService.reset(config.id);
      setConfigs((prev) =>
        prev.map((c) =>
          c.id === config.id
            ? { ...c, fieldCount: updated.fields.length, version: updated.version, updatedAt: updated.updatedAt }
            : c,
        ),
      );
    } catch (e) {
      setError(getErrorMsg(e, 'Generate failed'));
    } finally {
      setGeneratingType(null);
    }
  };

  useEffect(() => {
    Promise.all([
      formConfigService.listAll(),
      listingCategoriesService.list(),
    ])
      .then(([cfgs, cats]) => { setConfigs(cfgs); setCategories(cats); })
      .catch((e) => setError(e?.message || 'Failed to load form configs'))
      .finally(() => setLoading(false));
  }, []);

  // Group configs by listingCategoryId; uncategorized goes last
  const categoryGroups: Array<{ category: ListingCategory | null; configs: FormConfigSummary[] }> = [
    ...categories.map((cat) => ({
      category: cat,
      configs: configs.filter((c) => c.listingCategoryId === cat.id),
    })),
    {
      category: null,
      configs: configs.filter((c) => !c.listingCategoryId),
    },
  ];

  return (
    <Layout title={t('form_config')}>
      <div className="space-y-8">
        <Card className="p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">{t('form_config')}</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Manage field definitions for all listing form types, organised by listing category. Changes apply immediately to mobile.
              </p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              disabled={flushing}
              onClick={handleFlushCache}
            >
              {flushing ? '…' : t('flush_cache')}
            </Button>
          </div>
        </Card>

        {flushSuccess && (
          <div className="p-3 rounded bg-green-50 text-green-700 border border-green-100 text-sm dark:bg-green-900/20 dark:text-green-400 dark:border-green-800">
            {t('flush_cache_success')}
          </div>
        )}

        {error && (
          <div className="p-3 rounded bg-red-50 text-red-600 border border-red-100 text-sm">{error}</div>
        )}

        {loading ? (
          <div className="text-center py-12 text-gray-400">{t('loading')}</div>
        ) : (
          <div className="space-y-8">
            {categoryGroups.map(({ category, configs: groupConfigs }) => {
              const groupKey = category?.id ?? UNCATEGORIZED_KEY;
              const groupLabel = category ? category.key : 'Uncategorized';

              return (
                <div key={groupKey}>
                  <div className="flex items-center gap-2 mb-3">
                    <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100">
                      {groupLabel}
                    </h2>
                    <span className="text-xs text-gray-400 dark:text-gray-500 font-normal">
                      — {groupConfigs.length} form type{groupConfigs.length !== 1 ? 's' : ''}
                    </span>
                    <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700 ml-1" />
                  </div>

                  {groupConfigs.length === 0 ? (
                    <p className="text-sm text-gray-400 pl-1">No form configs assigned to this category.</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {groupConfigs.map((config) => (
                        <ConfigCard
                          key={config.id}
                          config={config}
                          generating={generatingType === config.formType}
                          onGenerate={() => handleGenerateDefaults(config)}
                          onEdit={() => navigate(`/form-config/${config.formType}`)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      {ConfirmDialog}
    </Layout>
  );
};

export default FormConfigList;
